import clsx from "clsx";
import { useEqualizationRetrieveSuspense } from "gen/endpoints/equalization/equalization";
import { useTranslation } from "react-i18next";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { getRootGenericTableColumFromAgColumn } from "shared/components/table/data/DataTableUtils";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const EqualizationPage: React.FC = () => {
  const { t } = useTranslation(["common", "equalization", "capitalActivity"]);

  const { fundFamilyId, fundFamilyEventId, fundFamilySubEventId } =
    useRequiredStringParams();

  const { data: equalizationBreakdownTableData } =
    useEqualizationRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
      fundFamilySubEventId,
    );

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={t("equalization:reviewEqualization")}
        backButton={{
          title: t("capitalActivity:capitalEvents"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({
              fundFamilyEventId,
            }).$,
        }}
      />
      <PageBody>
        <TableBox isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:equalizations")}</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={equalizationBreakdownTableData}
              sx={{
                [".ag-header-row-column-group:first-of-type " +
                ".ag-header-group-cell " +
                ".mb-header-cell-comp"]: {
                  bg: "white",
                  px: "8px !important",
                  justifyContent: "flex-start !important",
                  "& .mb-header-cell-comp-title": {
                    color: "black",
                  },
                },
                ".mb-equalization-totals": {
                  bg: "primary.50 !important",
                  color: "primary.600",
                  "&.mb-cell-value-type-money": {
                    ".mb-header-cell-comp-title[data-currency-symbol]": {
                      _after: {
                        color: "primary.400",
                      },
                    },
                  },
                },
              }}
              headerOrCellClass={(props) =>
                clsx({
                  "mb-equalization-totals":
                    ("columnGroup" in props &&
                      props.columnGroup?.getGroupId() ===
                        "current_equalization_totals") ||
                    props.column?.getColId() ===
                      "current_equalization_totals" ||
                    getRootGenericTableColumFromAgColumn(props.column)?.id ===
                      "current_equalization_totals",
                })
              }
            />
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};
